/**
 * EmojiVerificationScreen - Reusable emoji comparison component
 * 
 * Can be used for any verification type (device, user, cross-signing)
 * Fully isolated, presentational component with no Matrix SDK dependencies
 */

import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';
import './EmojiVerificationScreen.css';

// SAS emoji mapping (standard Matrix emoji set)
const SAS_EMOJI_MAP = {
  0: { emoji: '🐶', name: 'Dog' },
  1: { emoji: '🐱', name: 'Cat' },
  2: { emoji: '🦁', name: 'Lion' },
  3: { emoji: '🐴', name: 'Horse' },
  4: { emoji: '🦄', name: 'Unicorn' },
  5: { emoji: '🐷', name: 'Pig' },
  6: { emoji: '🐘', name: 'Elephant' },
  7: { emoji: '🐰', name: 'Rabbit' },
  8: { emoji: '🐼', name: 'Panda' },
  9: { emoji: '🐓', name: 'Rooster' },
  10: { emoji: '🐧', name: 'Penguin' },
  11: { emoji: '🐢', name: 'Turtle' },
  12: { emoji: '🐟', name: 'Fish' },
  13: { emoji: '🐙', name: 'Octopus' },
  14: { emoji: '🦋', name: 'Butterfly' },
  15: { emoji: '🌷', name: 'Flower' },
  16: { emoji: '🌳', name: 'Tree' },
  17: { emoji: '🌵', name: 'Cactus' },
  18: { emoji: '🍄', name: 'Mushroom' },
  19: { emoji: '🌏', name: 'Globe' },
  20: { emoji: '🌙', name: 'Moon' },
  21: { emoji: '☁️', name: 'Cloud' },
  22: { emoji: '🔥', name: 'Fire' },
  23: { emoji: '🍌', name: 'Banana' },
  24: { emoji: '🍎', name: 'Apple' },
  25: { emoji: '🍓', name: 'Strawberry' },
  26: { emoji: '🌽', name: 'Corn' },
  27: { emoji: '🍕', name: 'Pizza' },
  28: { emoji: '🎂', name: 'Cake' },
  29: { emoji: '❤️', name: 'Heart' },
  30: { emoji: '😀', name: 'Smiley' },
  31: { emoji: '🤖', name: 'Robot' },
  32: { emoji: '🎩', name: 'Hat' },
  33: { emoji: '👓', name: 'Glasses' },
  34: { emoji: '🔧', name: 'Wrench' },
  35: { emoji: '🎅', name: 'Santa' },
  36: { emoji: '👍', name: 'Thumbs Up' },
  37: { emoji: '☂️', name: 'Umbrella' },
  38: { emoji: '⌛', name: 'Hourglass' },
  39: { emoji: '⏰', name: 'Clock' },
  40: { emoji: '🎁', name: 'Gift' },
  41: { emoji: '💡', name: 'Light Bulb' },
  42: { emoji: '📕', name: 'Book' },
  43: { emoji: '✏️', name: 'Pencil' },
  44: { emoji: '📎', name: 'Paperclip' },
  45: { emoji: '✂️', name: 'Scissors' },
  46: { emoji: '🔒', name: 'Lock' },
  47: { emoji: '🔑', name: 'Key' },
  48: { emoji: '🔨', name: 'Hammer' },
  49: { emoji: '📞', name: 'Telephone' },
  50: { emoji: '🏁', name: 'Flag' },
  51: { emoji: '🚂', name: 'Train' },
  52: { emoji: '🚲', name: 'Bicycle' },
  53: { emoji: '✈️', name: 'Airplane' },
  54: { emoji: '🚀', name: 'Rocket' },
  55: { emoji: '🏆', name: 'Trophy' },
  56: { emoji: '⚽', name: 'Ball' },
  57: { emoji: '🎸', name: 'Guitar' },
  58: { emoji: '🎺', name: 'Trumpet' },
  59: { emoji: '🔔', name: 'Bell' },
  60: { emoji: '⚓', name: 'Anchor' },
  61: { emoji: '🎧', name: 'Headphones' },
  62: { emoji: '📁', name: 'Folder' },
  63: { emoji: '📌', name: 'Pin' },
};

/**
 * EmojiVerificationScreen Component
 * 
 * Displays emoji comparison UI for SAS verification
 * 
 * @param {(number[]|Array<[string, string]>)} emojis - Array of emoji indices OR [emoji, name] tuples
 * @param {Function} onConfirm - Callback when user confirms match
 * @param {Function} onReject - Callback when user rejects match
 * @param {boolean} isWaiting - Whether to show waiting state
 * @param {boolean} isConfirming - Whether confirm is in progress
 * @param {string} contextText - Optional context text (device name, user name)
 * @param {string} verificationType - Type of verification ('device' or 'user')
 */
const EmojiVerificationScreen = ({
  emojis,
  onConfirm,
  onReject,
  isWaiting = false,
  isConfirming = false,
  contextText = null,
  verificationType = 'device',
}) => {
  const { t } = useI18n();

  // Validate and map emojis
  // Supports both formats:
  // - Legacy: [0, 5, 12, ...] (numeric indices)
  // - Rust crypto: [['🐶', 'Dog'], ['🐱', 'Cat'], ...] (tuples)
  const emojiData = useMemo(() => {
    if (!Array.isArray(emojis) || emojis.length === 0) {
      return [];
    }

    return emojis.map((item) => {
      // Check if item is a tuple [emoji, name] (Rust crypto format)
      if (Array.isArray(item) && item.length === 2) {
        // Find the index in SAS_EMOJI_MAP by matching emoji character
        const index = Object.keys(SAS_EMOJI_MAP).find(k => SAS_EMOJI_MAP[k].emoji === item[0]);
        return { emoji: item[0], name: index !== undefined ? t(`SasEmoji.${index}`) : item[1] };
      }
      // Otherwise treat as index (legacy format)
      const entry = SAS_EMOJI_MAP[item];
      return entry ? { emoji: entry.emoji, name: t(`SasEmoji.${item}`) } : { emoji: '❓', name: t('SasEmoji.unknown') };
    });
  }, [emojis, t]);

  // Generate instructions based on verification type
  const instructions = useMemo(() => {
    if (contextText) {
      return t('EmojiVerificationScreen.instructionsWithContext', { contextText }, `Compare the following emojis with ${contextText}:`);
    }
    
    if (verificationType === 'user') {
      return t('EmojiVerificationScreen.instructionsUser', {}, 'Compare the following emojis with the other user:');
    }
    
    return t('EmojiVerificationScreen.instructionsDevice', {}, 'Compare the following emojis with your other device:');
  }, [contextText, verificationType, t]);

  // Show waiting state
  if (isWaiting) {
    return (
      <div className="emoji-verification-screen">
        <div className="emoji-verification-waiting">
          <div className="spinner" />
          <p>{t('EmojiVerificationScreen.waitingForEmojis', {}, 'Waiting for emojis to appear...')}</p>
        </div>
      </div>
    );
  }

  // Show error if no emojis
  if (emojiData.length === 0) {
    return (
      <div className="emoji-verification-screen">
        <div className="emoji-verification-error">
          <p>{t('EmojiVerificationScreen.noEmojisToDisplay', {}, 'No emojis to display')}</p>
        </div>
      </div>
    );
  }
  return (
    <div className="emoji-verification-screen">
      <div className="emoji-verification-content">
        <p className="emoji-verification-instructions">
          {instructions}
        </p>

        <div className="emoji-verification-emojis">
          {emojiData.map((data, index) => (
            <div key={index} className="emoji-verification-item">
              <span className="emoji-verification-icon">{data.emoji}</span>
              <span className="emoji-verification-name">{data.name}</span>
            </div>
          ))}
        </div>

        <div className="emoji-verification-actions">
          <button
            className="btn emoji-verification-reject"
            onClick={onReject}
            disabled={isConfirming}
          >
            {t('EmojiVerificationScreen.theyDontMatch', {}, 'They don\'t match')}
          </button>
          <button
            className="btn emoji-verification-confirm"
            onClick={onConfirm}
            disabled={isConfirming}
          >
            {isConfirming ? t('EmojiVerificationScreen.confirming', {}, 'Confirming...') : t('EmojiVerificationScreen.theyMatch', {}, 'They match')}
          </button>
        </div>
      </div>
    </div>
  );
};

EmojiVerificationScreen.propTypes = {
  emojis: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.number),  // Legacy: [0, 5, 12, ...]
    PropTypes.arrayOf(PropTypes.array),    // Rust: [['🐶', 'Dog'], ...]
  ]),
  onConfirm: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  isWaiting: PropTypes.bool,
  isConfirming: PropTypes.bool,
  contextText: PropTypes.string,
  verificationType: PropTypes.oneOf(['device', 'user']),
};

export default EmojiVerificationScreen;
export { SAS_EMOJI_MAP };
